/**
 * Default values for element properties
 * This file centralizes all default values to avoid duplication
 */

export const DEFAULT_SPACING = {
  padding: {
    top: 4,
    right: 4,
    bottom: 4,
    left: 4,
  },
  margin: {
    top: 15,
    right: 0,
    bottom: 0,
    left: 0,
  },
} as const;

export const DEFAULT_FIRST_ELEMENT_AFTER_HEADER_MARGIN_TOP = 30;

export const DEFAULT_COLORS = {
  text: '#2f2f2f',
  background: 'transparent',
} as const;

export const DEFAULT_FONT = {
  size: 10,
  weight: 'normal' as const,
  style: 'normal' as const,
  decoration: 'none' as const,
} as const;

/**
 * Default font weight for elements whose font styling targets their label.
 * Field labels render bold by default.
 */
export const DEFAULT_LABEL_FONT_WEIGHT = 'bold' as const;

export { DEFAULT_HEADER_MAIN_LABEL, DEFAULT_HEADER_MARGIN, HEADER_HORIZONTAL_MARGIN } from '@record-fill';

type DefaultValueType = 'spacing' | 'colors' | 'font';

/**
 * Returns default values for element creation
 * @param exclude - Array of default types to exclude from the return
 * @returns Object with default spacing, colors, and font properties
 *
 * @example
 * // Get all defaults
 * getDefaultValues()
 *
 * @example
 * // Get defaults without font
 * getDefaultValues(['font'])
 *
 * @example
 * // Get only spacing
 * getDefaultValues(['colors', 'font'])
 */
export function getDefaultValues(exclude: DefaultValueType[] = []) {
  const result: any = {};

  if (!exclude.includes('spacing')) {
    Object.assign(result, DEFAULT_SPACING);
  }

  if (!exclude.includes('colors')) {
    result.color = DEFAULT_COLORS.text;
    result.backgroundColor = DEFAULT_COLORS.background;
  }

  if (!exclude.includes('font')) {
    result.fontSize = DEFAULT_FONT.size;
    result.fontWeight = DEFAULT_FONT.weight;
    result.fontStyle = DEFAULT_FONT.style;
    result.textDecoration = DEFAULT_FONT.decoration;
  }

  return result;
}

/**
 * Returns default values for elements whose font styling targets their label
 * (see LABEL_FONT_ELEMENTS in useElementContainerStyles). Same as
 * getDefaultValues, but the label renders bold by default.
 */
export function getLabelElementDefaults(exclude: DefaultValueType[] = []) {
  return {
    ...getDefaultValues(exclude),
    fontWeight: DEFAULT_LABEL_FONT_WEIGHT,
  };
}
